import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from '@shared/components/bootstrap';
import { useDebounce } from '@shared/hooks';

import { Panel } from '@admin/components/helpers/panel';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { Ticket__TicketableEnum, Ticket__TicketableInput, useTicketablesQuery } from '@admin/schema';

import { TicketForm } from './form';

export const NewTicket: React.FC = () => {
  useTitle('New Ticket');
  const [selectedTicketable, setSelectedTicketable] = useState<
    (Ticket__TicketableInput & { ticketableName?: string }) | null
  >(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery);

  const { data, loading } = useTicketablesQuery({
    client,
    variables: { query: debouncedQuery },
    skip: !debouncedQuery || debouncedQuery.length < 2,
  });

  const handleSelectTicketable = (result: {
    ticketableID: string;
    ticketableType: Ticket__TicketableEnum;
    displayName?: string | null;
    name?: string | null;
  }) => {
    const ticketable: Ticket__TicketableInput & { ticketableName?: string } = {
      id: String(result.ticketableID),
      type: result.ticketableType,
      ticketableName: result.displayName || result.name || undefined,
    };

    setSelectedTicketable(ticketable);
  };

  const handleBackToSearch = () => {
    setSelectedTicketable(null);
    setSearchQuery('');
  };

  const ticketables = data?.ticketables || [];

  return (
    <>
      <h1 className="page-header text-overflow">
        <ol className="breadcrumb">
          <li>
            <NavLink to="/">Inbox</NavLink>
          </li>
          {selectedTicketable ? (
            <>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleBackToSearch();
                  }}
                >
                  Select Ticket Subject
                </a>
              </li>
              <li className="active">New Ticket</li>
            </>
          ) : (
            <li className="active">Select Ticket Subject</li>
          )}
        </ol>
      </h1>

      {!selectedTicketable && (
        <Panel>
          <Panel.Header>
            <Panel.Title>Select Ticket Subject</Panel.Title>
          </Panel.Header>
          <Panel.Body>
            <div className="form-group">
              <label htmlFor="ticketable-search">Search for an entity to create a ticket for:</label>
              <input
                id="ticketable-search"
                type="text"
                className="form-control"
                placeholder="Search by name, email, or number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            {loading && (
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin /> Searching...
              </div>
            )}

            {ticketables.length > 0 && (
              <div className="list-group">
                {ticketables.map((ticketable) => (
                  <button
                    key={`${ticketable.ticketableType}-${ticketable.ticketableID}`}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectTicketable(ticketable);
                    }}
                  >
                    <div className="d-flex w-100 justify-content-between">
                      <h6 className="mb-1">{ticketable.name || ticketable.displayName}</h6>
                      <small className="text-muted">{ticketable.ticketableType}</small>
                    </div>
                    {ticketable.email && <small className="text-muted">{ticketable.email}</small>}
                    {ticketable.number && <small className="text-muted">#{ticketable.number}</small>}
                  </button>
                ))}
              </div>
            )}

            {debouncedQuery && debouncedQuery.length >= 2 && !loading && ticketables.length === 0 && (
              <div className="alert alert-info">
                No entities found matching "{debouncedQuery}". Try a different search term.
              </div>
            )}

            {(!debouncedQuery || debouncedQuery.length < 2) && (
              <div className="text-muted">
                Enter at least 2 characters to search for accounts, orders, users, and other entities.
              </div>
            )}
          </Panel.Body>
          <Panel.Footer align="right">
            <Button
              kind="default"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Cancel
            </Button>
          </Panel.Footer>
        </Panel>
      )}

      {selectedTicketable && (
        <TicketForm
          ticketable={selectedTicketable}
          onClose={() => {
            window.location.href = '/';
          }}
        />
      )}
    </>
  );
};
